import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { 
  Users, FileText, StickyNote, Phone, Mail, Eye, Edit2, Save,
  DollarSign, Globe
} from 'lucide-react';
import { COMPANY } from '../../../config/company';

const ClientOverviewTab = ({
  language,
  client,
  clientEstimates,
  contacts,
  accountingSummary,
  accountingInvoices,
  // Edit State
  isEditing,
  setIsEditing,
  editData,
  setEditData,
  // Handlers
  handleInputChange,
  handleSaveClient,
  setActiveTab,
  // Utilities
  formatDate,
  formatCurrency
}) => {
  return (
    <div className="space-y-6">
      {/* Client Contact Information Card */}
      <Card className="bg-white border-gray-200">
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Users className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Información del Cliente' : 'Client Information'}
            </span>
            <Button size="sm" variant="secondary" onClick={() => setIsEditing(!isEditing)}>
              <Edit2 className="h-4 w-4 mr-1" />{isEditing ? (language === 'es' ? 'Cancelar' : 'Cancel') : (language === 'es' ? 'Editar' : 'Edit')}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Contact Details */}
            <div className="space-y-4">
              <div>
                <Label className="text-gray-500 text-xs">{language === 'es' ? 'Nombre Completo' : 'Full Name'}</Label>
                {isEditing ? (
                  <Input value={editData.name || client.name} onChange={(e) => setEditData({...editData, name: e.target.value})} />
                ) : (
                  <p className="font-semibold text-lg">{client.name || clientEstimates[0]?.client_name || '—'}</p>
                )}
              </div>
              <div>
                <Label className="text-gray-500 text-xs">{language === 'es' ? 'Dirección' : 'Street Address'}</Label>
                {isEditing ? (
                  <Input value={editData.address || client.address} onChange={(e) => setEditData({...editData, address: e.target.value})} placeholder={language === 'es' ? '123 Calle Principal' : '123 Main Street'} />
                ) : (
                  <p className="text-gray-700">{client.address || clientEstimates[0]?.client_address || <span className="text-gray-400 italic">{language === 'es' ? 'Sin dirección' : 'No address'}</span>}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-gray-500 text-xs">{language === 'es' ? 'Ciudad' : 'City'}</Label>
                  {isEditing ? (
                    <Input value={editData.city !== undefined ? editData.city : (client.city || '')} onChange={(e) => setEditData({...editData, city: e.target.value})} placeholder="Miami" />
                  ) : (
                    <p className="text-gray-700">{client.city || clientEstimates[0]?.client_city || <span className="text-gray-400 italic">—</span>}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">{language === 'es' ? 'Estado' : 'State'}</Label>
                  {isEditing ? (
                    <Input value={editData.state !== undefined ? editData.state : (client.state || 'FL')} onChange={(e) => setEditData({...editData, state: e.target.value})} placeholder="FL" />
                  ) : (
                    <p className="text-gray-700">{client.state || 'FL'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">{language === 'es' ? 'Código Postal' : 'ZIP Code'}</Label>
                  {isEditing ? (
                    <Input value={editData.zip_code !== undefined ? editData.zip_code : (client.zip_code || '')} onChange={(e) => setEditData({...editData, zip_code: e.target.value})} placeholder="33101" />
                  ) : (
                    <p className="text-gray-700">{client.zip_code || <span className="text-gray-400 italic">—</span>}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                  {isEditing ? (
                    <Input value={editData.phone || client.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} />
                  ) : (
                    <p className="text-gray-700 flex items-center"><Phone className="h-4 w-4 mr-2 text-gray-400" />{client.phone || clientEstimates[0]?.client_phone || '—'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">{language === 'es' ? 'Email' : 'Email'}</Label>
                  {isEditing ? (
                    <Input value={editData.email || client.email} onChange={(e) => setEditData({...editData, email: e.target.value})} />
                  ) : (
                    <p className="text-gray-700 flex items-center"><Mail className="h-4 w-4 mr-2 text-gray-400" />{client.email || clientEstimates[0]?.client_email || '—'}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Website</Label>
                  {isEditing ? (
                    <Input type="url" value={editData.website !== undefined ? editData.website : (client.website || '')} onChange={(e) => setEditData({...editData, website: e.target.value})} placeholder="https://www.example.com" />
                  ) : (
                    client.website ? (
                      <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center"><Globe className="h-4 w-4 mr-2 text-gray-400" />{client.website}</a>
                    ) : (
                      <p className="text-gray-400 italic flex items-center"><Globe className="h-4 w-4 mr-2 text-gray-300" />—</p>
                    )
                  )}
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Google Business Profile</Label>
                  {isEditing ? (
                    <Input type="url" value={editData.google_business_profile !== undefined ? editData.google_business_profile : (client.google_business_profile || '')} onChange={(e) => setEditData({...editData, google_business_profile: e.target.value})} placeholder="https://g.page/business-name" />
                  ) : (
                    client.google_business_profile ? (
                      <a href={client.google_business_profile} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline flex items-center"><Globe className="h-4 w-4 mr-2 text-green-500" />Google Business</a>
                    ) : (
                      <p className="text-gray-400 italic flex items-center"><Globe className="h-4 w-4 mr-2 text-gray-300" />—</p>
                    )
                  )}
                </div>
              </div>
              {isEditing && (
                <Button onClick={handleSaveClient} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                  <Save className="h-4 w-4 mr-1" />{language === 'es' ? 'Guardar' : 'Save'}
                </Button>
              )}
            </div>
            
            {/* Right Column - Client Status */}
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <Label className="text-gray-500 text-xs">{language === 'es' ? 'Número de Cliente' : 'Client Number'}</Label>
                <p className="font-mono text-sm font-semibold text-amber-600">{client.client_number || '—'}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">{language === 'es' ? 'Estado' : 'Status'}</Label>
                <Badge className={
                  client.status === 'active' ? 'bg-green-100 text-green-700' : 
                  client.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  client.status === 'closed' ? 'bg-purple-100 text-purple-700' :
                  client.status === 'overdue' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }>
                  {client.status === 'active' ? (language === 'es' ? 'Activo' : 'Active') : 
                   client.status === 'in_progress' ? (language === 'es' ? 'En Progreso' : 'In Progress') :
                   client.status === 'closed' ? (language === 'es' ? 'Cerrado' : 'Closed') :
                   client.status === 'overdue' ? (language === 'es' ? 'Vencido' : 'Overdue') :
                   (language === 'es' ? 'Archivado' : 'Archived')}
                </Badge>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">{language === 'es' ? 'Categoría del Proyecto' : 'Project Category'}</Label>
                <p className="text-gray-700 capitalize font-medium">
                  {(client.project_category || clientEstimates[0]?.project_type || clientEstimates[0]?.project_category || '').replace(/_/g, ' ') || <span className="text-gray-400 italic">{language === 'es' ? 'Sin categoría' : 'No category'}</span>}
                </p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">{language === 'es' ? 'Fuente' : 'Source'}</Label>
                <p className="text-gray-700 capitalize">{client.source || clientEstimates[0]?.lead_source || 'Direct'}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">{language === 'es' ? 'Fecha de Registro' : 'Date Registered'}</Label>
                <p className="text-gray-700">{formatDate(client.created_at)}</p>
              </div>
              
              {/* Client Identifier Flags */}
              <div className="pt-3 border-t">
                <Label className="text-gray-500 text-xs mb-2 block">{language === 'es' ? 'Identificadores' : 'Client Flags'}</Label>
                <div className="space-y-2">
                  {/* VIP Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">⭐ VIP</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={client.is_vip === true}
                        onChange={(e) => handleInputChange('is_vip', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                  
                  {/* Repeat Client Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{language === 'es' ? 'Repetido' : 'Repeat Client'}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={client.is_repeat === true}
                        onChange={(e) => handleInputChange('is_repeat', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                  </div>
                  
                  {/* Hot Lead Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">🔥 {language === 'es' ? 'Lead Caliente' : 'Hot Lead'}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={client.is_hot_lead === true}
                        onChange={(e) => handleInputChange('is_hot_lead', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
                    </label>
                  </div>
                  
                  {/* Insurance Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{language === 'es' ? 'Seguro' : 'Insurance'}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={client.is_insurance === true}
                        onChange={(e) => handleInputChange('is_insurance', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
                    </label>
                  </div>
                  
                  {/* Closed Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{language === 'es' ? 'Cerrado' : 'Closed'}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={client.status === 'closed'}
                        onChange={(e) => handleInputChange('status', e.target.checked ? 'closed' : 'active')}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-gray-500"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project/Estimate Information */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="bg-blue-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center">
            <FileText className="mr-2 h-5 w-5" />
            {language === 'es' ? 'Información del Proyecto' : 'Project Information'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {clientEstimates.length > 0 ? (
            <div className="space-y-4">
              {clientEstimates.map((estimate, idx) => (
                <div key={estimate.id} className={`p-4 rounded-lg border ${idx === 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-lg flex items-center">
                        {estimate.proposal_id || estimate.estimate_number}
                        {idx === 0 && <Badge className="ml-2 bg-blue-100 text-blue-700">{language === 'es' ? 'Principal' : 'Primary'}</Badge>}
                      </p>
                      <p className="text-sm text-gray-500">{formatDate(estimate.estimate_date || estimate.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(estimate.grand_total || 0)}</p>
                      <Badge className={
                        estimate.status === 'won' ? 'bg-green-100 text-green-700' :
                        estimate.status === 'lost' ? 'bg-red-100 text-red-700' :
                        estimate.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {estimate.status || 'New'}
                      </Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">{language === 'es' ? 'Categoría' : 'Category'}:</span>
                      <p className="font-medium capitalize">{(estimate.project_type || '').replace(/_/g, ' ') || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">{language === 'es' ? 'Tipo' : 'Type'}:</span>
                      <p className="font-medium capitalize">{estimate.estimate_type || 'Itemized'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">{language === 'es' ? 'Items' : 'Items'}:</span>
                      <p className="font-medium">{(estimate.line_items?.length || 0) + (estimate.lump_sum_items?.length || 0)}</p>
                    </div>
                    <div>
                      <Link to={`/leads/${estimate.id}`}>
                        <Button size="sm" variant="outline">
                          <Eye className="h-4 w-4 mr-1" />{language === 'es' ? 'Ver Detalles' : 'View Details'}
                        </Button>
                      </Link>
                    </div>
                  </div>
                  {estimate.project_description && (
                    <p className="mt-3 text-sm text-gray-600 border-t pt-3">{estimate.project_description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{language === 'es' ? 'No hay proyectos vinculados' : 'No linked projects'}</p>
              <p className="text-sm">{language === 'es' ? 'Los presupuestos aparecerán aquí cuando se vinculen' : 'Estimates will appear here when linked'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Financial Summary Card */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="bg-green-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <DollarSign className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Resumen Financiero' : 'Financial Summary'}
            </span>
            <Button size="sm" variant="secondary" onClick={() => setActiveTab('accounting')}>
              {language === 'es' ? 'Ver Contabilidad' : 'View Accounting'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-500">{language === 'es' ? 'Total Contratos' : 'Total Contracts'}</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(accountingSummary?.contract_total || clientEstimates.reduce((sum, e) => sum + (e.grand_total || 0), 0))}</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-500">{language === 'es' ? 'Pagos Recibidos' : 'Payments Received'}</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(accountingSummary?.total_payments || 0)}</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-500">{language === 'es' ? 'Saldo Pendiente' : 'Outstanding'}</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(accountingSummary?.outstanding_balance || clientEstimates.reduce((sum, e) => sum + (e.grand_total || 0), 0))}</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-500">{language === 'es' ? 'Facturas' : 'Invoices'}</p>
              <p className="text-2xl font-bold text-purple-600">{accountingInvoices.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-gray-900 text-lg">{language === 'es' ? 'Resumen' : 'Summary'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between"><span className="text-gray-600">{language === 'es' ? 'Proyectos' : 'Projects'}</span><span className="text-gray-900 font-semibold">{clientEstimates.length}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">{language === 'es' ? 'Contactos' : 'Contacts'}</span><span className="text-gray-900 font-semibold">{contacts.length}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">{language === 'es' ? 'Notas' : 'Notes'}</span><span className="text-gray-900 font-semibold">{client.notes?.length || 0}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">{language === 'es' ? 'Documentos' : 'Documents'}</span><span className="text-gray-900 font-semibold">{client.documents?.length || 0}</span></div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 md:col-span-2">
          <CardHeader><CardTitle className="text-gray-900 text-lg">{language === 'es' ? 'Actividad Reciente' : 'Recent Activity'}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[...clientEstimates.slice(0, 2), ...(client.notes?.slice(0, 2) || [])].sort((a, b) => new Date(b.created_at || b.estimate_date) - new Date(a.created_at || a.estimate_date)).slice(0, 4).map((item, i) => (
                <div key={i} className="flex items-center space-x-3 p-2 rounded bg-gray-100">
                  {item.estimate_number ? <FileText className="h-5 w-5 text-amber-500" /> : <StickyNote className="h-5 w-5 text-blue-500" />}
                  <div className="flex-1">
                    <p className="text-gray-900 text-sm">{item.estimate_number || item.proposal_id || item.content?.substring(0, 50) + '...'}</p>
                    <p className="text-gray-500 text-xs">{formatDate(item.created_at || item.estimate_date)}</p>
                  </div>
                  {(item.estimate_number || item.proposal_id) && (
                    <Link to={`/leads/${item.id}`}>
                      <Button size="sm" variant="ghost"><Eye className="h-4 w-4" /></Button>
                    </Link>
                  )}
                </div>
              ))}
              {(clientEstimates.length === 0 && !client.notes?.length) && <p className="text-gray-500 text-center py-4">{language === 'es' ? 'Sin actividad' : 'No activity yet'}</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientOverviewTab;
